import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { CalendarDays, Users, TrendingUp, MapPin, Ticket, Sparkles, Timer, Heart } from "lucide-react";
import { formatDate } from "@/lib/format";

export default function VenueOverview() {
    const { venue } = useOutletContext();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!venue?.id) return;
        setLoading(true);
        endpoints.venueOverview(venue.id).then(setData).finally(() => setLoading(false));
    }, [venue?.id]);

    if (loading || !data) return <div className="p-8 font-mono text-xs text-[#666]" data-testid="venue-overview-loading">Loading…</div>;
    const s = data.summary;
    const fm = data.follower_metrics || {};

    return (
        <div className="pm-fade" data-testid="venue-overview">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Overview</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">{data.venue.name}</h1>
                    <div className="mt-1 text-xs font-mono uppercase text-[#a3a3a3] flex items-center gap-1"><MapPin className="w-3 h-3" /> {data.venue.city}, {data.venue.country}</div>
                </div>
                <Link to={`/venue/${venue.id}/events/new`} className="pm-btn pm-btn-primary" data-testid="venue-new-event-btn">+ Create Event</Link>
            </header>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]" data-testid="venue-summary">
                <Kpi icon={CalendarDays} label="Upcoming" value={s.upcoming_events} testId="v-kpi-upcoming" />
                <Kpi icon={Timer} label="Completed" value={s.completed_events} testId="v-kpi-completed" />
                <Kpi icon={Ticket} label="Registrations" value={s.total_registrations} testId="v-kpi-regs" />
                <Kpi icon={Users} label="Attendance" value={s.total_attendance} testId="v-kpi-att" />
                <Kpi icon={TrendingUp} label="Avg attendance" value={`${s.avg_attendance_rate}%`} testId="v-kpi-avgatt" />
                <Kpi icon={Users} label="Returning" value={s.returning_attendees} testId="v-kpi-return" />
                <Kpi icon={Sparkles} label="Capacity util." value={`${s.capacity_utilization}%`} testId="v-kpi-cap" />
                <Kpi icon={CalendarDays} label="Draft / pending" value={s.draft_or_pending} testId="v-kpi-draft" />
            </section>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]" data-testid="venue-follower-metrics">
                <Kpi icon={Heart} label="Followers" value={fm.current_followers || 0} testId="v-kpi-followers" />
                <Kpi icon={TrendingUp} label="New · 30 days" value={fm.new_last_30_days || 0} testId="v-kpi-followers-30" />
                <Kpi icon={Sparkles} label="Recent event gain" value={fm.most_recent_event_growth ? fm.most_recent_event_growth.window_total : 0}
                     sub={fm.most_recent_event_growth?.event_name} testId="v-kpi-recent-event-growth" />
                <Kpi icon={TrendingUp} label="Best event growth" value={fm.best_event_by_growth ? fm.best_event_by_growth.window_total : 0}
                     sub={fm.best_event_by_growth?.event_name} testId="v-kpi-best-event-growth" />
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                <div className="bg-[#0a0a0a] p-6" data-testid="venue-upcoming-events">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Live</div>
                    <h2 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-3">Upcoming events</h2>
                    {data.upcoming_events.length === 0 ? (
                        <div className="text-sm text-[#a3a3a3]">No upcoming events at this venue.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.upcoming_events.map((e) => (
                                <Link to={`/venue/${venue.id}/events`} key={e.id} className="block border border-[#2a2a2a] p-3 hover:border-[#ff4400]" data-testid={`venue-upcoming-${e.id}`}>
                                    <div className="text-sm font-display font-bold uppercase truncate">{e.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{formatDate(e.date)} · {e.artist_name} · {e.registrations}/{e.capacity}</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
                <div className="bg-[#0a0a0a] p-6" data-testid="venue-best-event">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Highlight</div>
                    <h2 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-3">Strongest event</h2>
                    {data.best_event ? (
                        <div className="border border-[#2a2a2a] p-4">
                            <div className="font-display font-bold uppercase">{data.best_event.name}</div>
                            <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1">{formatDate(data.best_event.date)}</div>
                            <div className="mt-2 font-display font-black text-3xl text-[#ff4400]">{data.best_event.attendees}</div>
                            <div className="text-[10px] font-mono uppercase text-[#666]">attendees · capacity {data.best_event.capacity}</div>
                        </div>
                    ) : <div className="text-sm text-[#a3a3a3]">No completed events yet.</div>}
                </div>
            </section>
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub, testId }) {
    return (
        <div className="bg-[#0a0a0a] p-5" data-testid={testId}>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#666]"><Icon className="w-3 h-3" /> {label}</div>
            <div className="mt-2 font-display font-black uppercase tracking-tighter text-2xl md:text-3xl">{value}</div>
            {sub && <div className="mt-1 text-[10px] font-mono uppercase text-[#a3a3a3] truncate">{sub}</div>}
        </div>
    );
}
